/**
 * Graph Analytics Card Component
 * Displays PageRank, community detection, and centrality analysis
 */
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  Paper,
  Tabs,
  Tab,
  Card,
  CardContent
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Groups as GroupsIcon,
  AccountTree as AccountTreeIcon,
  Star as StarIcon
} from '@mui/icons-material';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`graph-tabpanel-${index}`}
      aria-labelledby={`graph-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function GraphAnalyticsCard({ graphData, loading }) {
  const [activeTab, setActiveTab] = useState(0);

  if (!graphData) {
    return (
      <Alert severity="info">
        No graph analytics data available. Run analytics to generate graph insights.
      </Alert>
    );
  }

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Graph Analytics
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Network analysis using Neo4j to identify influential users, communities, and key connectors.
        </Typography>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <TrendingUpIcon color="primary" />
                <Typography variant="h6">
                  PageRank
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Top {graphData.pagerank?.top_users?.length || 0} influential users identified
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Algorithm: {graphData.pagerank?.algorithm || 'N/A'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <GroupsIcon color="secondary" />
                <Typography variant="h6">
                  Communities
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {graphData.communities?.total_communities || 0} communities detected
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Based on group memberships
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <AccountTreeIcon color="info" />
                <Typography variant="h6">
                  Centrality
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {graphData.centrality?.central_users?.length || 0} key connectors identified
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Metric: {graphData.centrality?.metric || 'N/A'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabbed Analytics */}
      <Paper>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="PageRank" />
          <Tab label="Communities" />
          <Tab label="Centrality" />
        </Tabs>

        {/* PageRank Tab */}
        <TabPanel value={activeTab} index={0}>
          {graphData.pagerank?.top_users && graphData.pagerank.top_users.length > 0 ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Rank</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell align="right">Connections</TableCell>
                    {graphData.pagerank.top_users?.some(u => u.pagerank_score !== null && u.pagerank_score !== undefined) && (
                      <TableCell align="right">PageRank Score</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {graphData.pagerank.top_users.map((user, index) => (
                    <TableRow key={user.user_id}>
                      <TableCell>
                        <Chip
                          icon={index < 3 ? <StarIcon /> : null}
                          label={`#${index + 1}`}
                          color={index === 0 ? 'primary' : index < 3 ? 'secondary' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{user.display_name || user.user_id}</TableCell>
                      <TableCell>{user.email || '-'}</TableCell>
                      <TableCell align="right">{user.connections || 0}</TableCell>
                      {user.pagerank_score !== undefined && user.pagerank_score !== null && (
                        <TableCell align="right">{user.pagerank_score.toFixed(4)}</TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info">No PageRank data available.</Alert>
          )}
        </TabPanel>

        {/* Communities Tab */}
        <TabPanel value={activeTab} index={1}>
          {graphData.communities?.communities && graphData.communities.communities.length > 0 ? (
            <Grid container spacing={2}>
              {graphData.communities.communities.map((community, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <Paper sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <GroupsIcon color="primary" />
                      <Typography variant="h6">
                        {community.community_name}
                      </Typography>
                      <Chip label={`${community.size} members`} size="small" />
                    </Box>
                    <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                      {community.members.map((member, memberIndex) => (
                        <Chip
                          key={memberIndex}
                          label={member}
                          size="small"
                          sx={{ m: 0.5 }}
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Alert severity="info">No community data available.</Alert>
          )}
        </TabPanel>

        {/* Centrality Tab */}
        <TabPanel value={activeTab} index={2}>
          {graphData.centrality?.central_users && graphData.centrality.central_users.length > 0 ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Rank</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Job Title</TableCell>
                    <TableCell align="right">Paths Through</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {graphData.centrality.central_users.map((user, index) => (
                    <TableRow key={user.user_id}>
                      <TableCell>
                        <Chip
                          label={`#${index + 1}`}
                          color={index < 3 ? 'primary' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{user.display_name || user.user_id}</TableCell>
                      <TableCell>{user.email || '-'}</TableCell>
                      <TableCell>{user.job_title || '-'}</TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="bold">
                          {user.paths_through}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info">No centrality data available.</Alert>
          )}
        </TabPanel>
      </Paper>
    </Box>
  );
}

export default GraphAnalyticsCard;
